<script setup lang="ts">
import { getLegalDocuments, updateLegalDocumentsAcceptance } from '@/services/core.service'
import { useOIDCStore } from '@/stores/oidc.store'
import { LegalDocumentType } from '@/types/rc/forms'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useScroll } from '@vueuse/core'
import { ElLink } from 'element-plus'
import { isEmpty } from 'lodash'
import { marked } from 'marked'
import { computed, h, ref, toRefs } from 'vue'
import { useRouter } from 'vue-router'
import eulaMdContent from './EULA.md?raw'
import ppMdContent from './PRIVACY.md?raw'
import tosMdContent from './TOS.md?raw'

const { logout, profile, loadUserProfile } = useOIDCStore()
const router = useRouter()

const open = ref(true)

// User agrees
const checked = ref(false)

// BE State

const needsUpdate = computed(() => ({
  [LegalDocumentType.EULA]: profile && !profile.eula_accepted,
  [LegalDocumentType.PP]: profile && !profile.privacy_policy_accepted,
  [LegalDocumentType.TOS]: profile && !profile.tos_accepted,
}))

// Filter by value, keep the keys
const toUpdate = computed(
  () =>
    Object.entries(needsUpdate.value)
      .filter(([key, value]) => value) // eslint-disable-line @typescript-eslint/no-unused-vars
      .map(([key, value]) => key) as LegalDocumentType[] // eslint-disable-line @typescript-eslint/no-unused-vars
)

// Text content

const Header = computed(() => {
  if (isEmpty(docsURLs.value)) return h('p', {}, () => 'Loading...')

  const links = toUpdate.value.map((docName) =>
    h(
      ElLink,
      { href: docsURLs.value[docName], class: 'text-brand align-baseline', key: docName, target: '_blank' },
      () => docName
    )
  )

  const introText = h('span', {}, () => 'Please read the updated ')
  const outroPluralText = h('span', {}, () => ' documents carefully and accept.')
  const outroSingularText = h('span', {}, () => ' document carefully and accept.')

  // Wording versions
  const threeLinks = h('p', {}, () => [introText, links[0], ', ', links[1], ' and ', links[2], outroPluralText])
  const twoLinks = h('p', {}, () => [introText, links[0], ' and ', links[1], outroPluralText])
  const oneLink = h('p', {}, () => [introText, links[0], outroSingularText])

  const message = links.length === 3 ? threeLinks : links.length === 2 ? twoLinks : oneLink

  return message
})

const texts = {
  [LegalDocumentType.EULA]: marked(eulaMdContent),
  [LegalDocumentType.PP]: marked(ppMdContent),
  [LegalDocumentType.TOS]: marked(tosMdContent),
}

// Linked docs URLs
const { data: documentsUrls } = useQuery({
  queryKey: ['legal-documents'],
  queryFn: getLegalDocuments,
})

const docsURLs = computed(() =>
  documentsUrls.value ? Object.fromEntries(documentsUrls.value.map((doc) => [doc.document_name, doc.document_url])) : {}
)

// Scrollbar tracking

const scrollBarRef = ref()
const { arrivedState, y } = useScroll(scrollBarRef)
const { bottom: scrolledThrough } = toRefs(arrivedState)

const progress = computed(() => {
  if (!scrollBarRef.value) return 0
  if (scrolledThrough.value) return 100
  const p = (100 * y.value) / (scrollBarRef.value.scrollHeight - scrollBarRef.value.clientHeight)
  return isNaN(p) ? 0 : p
})

// Event handlers

const { mutate } = useMutation({
  mutationFn: updateLegalDocumentsAcceptance,
})

const updatePending = ref(false)

const accept = () => {
  if (scrolledThrough.value && checked.value) {
    updatePending.value = true
    mutate(
      {
        privacy_policy_accepted: needsUpdate.value[LegalDocumentType.PP] ?? undefined,
        eula_accepted: needsUpdate.value[LegalDocumentType.EULA] ?? undefined,
        tos_accepted: needsUpdate.value[LegalDocumentType.TOS] ?? undefined,
      },
      {
        onSuccess: () => {
          loadUserProfile().then(() => router.push('/'))
        },
      }
    )
  }
}

const onClosed = () => {
  if (updatePending.value) {
    updatePending.value = false
  }
}

const cancel = logout
</script>

<template>
  <el-dialog
    v-model="open"
    title="Terms and Conditions"
    :show-close="false"
    width="544px"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    @closed="onClosed"
  >
    <div v-loading="updatePending" class="flex flex-col gap-4">
      <!-- Top text -->
      <div class="flex flex-col gap-4">
        <p>Some of our terms and policies have been updated.</p>
        <Header v-if="documentsUrls" />
      </div>

      <!-- Actual T&Cs -->
      <el-progress :percentage="progress" :show-text="false" :status="scrolledThrough ? 'success' : undefined" />
      <div
        ref="scrollBarRef"
        class="bg-fill-primary text-secondary border-1 rounded-lg border-component-stroke-primary h-[400px] overflow-scroll"
      >
        <div v-for="doc in toUpdate" :key="doc" class="text-content px-3 pr-6 py-2 pt-8" v-html="texts[doc]" />
      </div>

      <!-- Checkboxes -->
      <div class="flex flex-col">
        <el-tooltip content="Please read the document in full." :disabled="scrolledThrough">
          <el-checkbox v-model="checked" class="w-max" :disabled="!scrolledThrough">
            <p class="text-tertiary font-normal">I agree to these documents</p>
          </el-checkbox>
        </el-tooltip>
      </div>

      <div class="flex justify-end">
        <el-button size="large" @click="() => cancel()"> Cancel </el-button>
        <el-button
          type="primary"
          size="large"
          :disabled="!checked || !scrolledThrough"
          :loading="updatePending"
          @click="accept"
        >
          Accept
        </el-button>
      </div>
    </div>
  </el-dialog>
</template>

<style scoped lang="scss">
.el-dialog {
  &__header {
    padding: 16px 24px;
    border-bottom: 1px solid #2a2a2a;
  }

  &__title {
    font-weight: 300;
    color: var(--text-component-label);
  }

  &__body {
    padding: 24px;
  }
}

.text-content {
  hr {
    margin-top: 32px;
    border-color: var(--text-component-label);
  }

  h2 {
    margin: 16px 0;
  }

  h3 {
    margin-top: 16px;
  }

  p {
    padding: 4px 0;
  }

  li {
    margin-left: 24px;
    list-style: disc;
  }
}
</style>
